import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { Avatar, Button, Card, Checkbox, Form, Input, List, Select, Tooltip } from 'antd';
import { comboColorMap, ORIENTATION_OPTIONS, POSTER_LANGUAGE_OPTIONS } from 'constants/constant';
import { ThumbnailInterface } from 'content/type';
import React from 'react';

interface ThumbnailListProps {
  thumbnails: ThumbnailInterface[];
  selectedThumbnail: ThumbnailInterface | null;
  onThumbnailSelect: (thumbnail: ThumbnailInterface | null) => void;
  onThumbnailRemove: (thumbnail: ThumbnailInterface) => void;
  onThumbnailChange: (thumbnail: ThumbnailInterface, key: string, value: string) => void;
  onNewThumbnailChange: (thumbnail: ThumbnailInterface, key: string, value: string) => void;
  canDownload?: boolean;
  isNew?: boolean;
}

// Function to normalize language value (convert lowercase/various formats to option format)
const normalizeLanguage = (lang: string | undefined): string | undefined => {
  if (!lang) return undefined;

  const normalized = lang.toUpperCase();

  // Special case handling
  const mapping: Record<string, string> = {
    KR: 'KO',
    JP: 'JA',
    ZH: 'ZH-HANS',
  };

  // Direct match case
  if (POSTER_LANGUAGE_OPTIONS.some(opt => opt.value === normalized)) {
    return normalized;
  }

  // Mapping required case
  if (mapping[normalized]) {
    return mapping[normalized];
  }

  // Return KO as default
  return 'KO';
};

const ThumbnailList: React.FC<ThumbnailListProps> = ({
  thumbnails,
  selectedThumbnail,
  onThumbnailSelect,
  onThumbnailRemove,
  onThumbnailChange,
  onNewThumbnailChange,
  canDownload,
  isNew,
}) => {
  const handleThumbnailDownload = async (thumbnail: ThumbnailInterface): Promise<void> => {
    try {
      const fileUrl = thumbnail.file || thumbnail.originFileObj?.name;

      if (!fileUrl) {
        throw new Error('No valid file URL found for download');
      }

      const response = await fetch(fileUrl as string);

      if (!response.ok) {
        throw new Error('File fetch failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      const fileName = fileUrl.split('/').pop();

      link.download = (fileName as string) || 'downloaded-thumbnail';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Sort by id descending (recently added items with larger id on top)
  const sortedThumbnails = [...thumbnails].sort((a, b) => {
    // Items without id go to bottom
    if (!a.id && !b.id) return 0;
    if (!a.id) return 1;
    if (!b.id) return -1;
    // Sort descending when id exists
    return (b.id || 0) - (a.id || 0);
  });

  return (
    <List
      itemLayout="vertical"
      dataSource={sortedThumbnails}
      renderItem={(thumbnail: ThumbnailInterface) => {
        const key = `${thumbnail.language}-${thumbnail.orientation}`;
        const bgColor = comboColorMap[key] || 'transparent';

        return (
          <Card className="upload-video-list-item" hoverable style={{ backgroundColor: bgColor }}>
            <div className="video-meta">
              <Form.Item>
                <Checkbox
                  checked={selectedThumbnail?.id === thumbnail.id}
                  onChange={e => {
                    if (e.target.checked) {
                      onThumbnailSelect(thumbnail);
                    } else {
                      onThumbnailSelect(null);
                    }
                  }}
                />
              </Form.Item>
              <Avatar
                shape="square"
                style={{ width: '100px', height: '100px', paddingLeft: '10px' }}
                src={thumbnail.file ? thumbnail.file : ''}
              />
              <div className="video-info">
                <Form.Item label="Name">
                  {canDownload ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Tooltip title={thumbnail.file?.split('/').pop()}>
                        <span
                          className="thumbnail-file-name"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            maxWidth: '300px',
                            verticalAlign: 'middle',
                          }}
                        >
                          {thumbnail.file?.split('/').pop()}
                        </span>
                      </Tooltip>
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => handleThumbnailDownload(thumbnail)}
                        style={{ padding: '0px' }}
                      >
                        Download
                      </Button>
                    </div>
                  ) : (
                    <Input type="text" value={thumbnail.file || ''} />
                  )}
                  <Form.Item label="Language">
                    <Select
                      value={normalizeLanguage(thumbnail.language)}
                      onChange={(value: string) =>
                        isNew
                          ? onNewThumbnailChange(thumbnail, 'language', value)
                          : onThumbnailChange(thumbnail, 'language', value)
                      }
                      style={{ width: 170 }}
                      options={POSTER_LANGUAGE_OPTIONS}
                    ></Select>
                  </Form.Item>

                  <Form.Item label="Orientation">
                    <Select
                      value={thumbnail.orientation}
                      onChange={(value: string) =>
                        isNew
                          ? onNewThumbnailChange(thumbnail, 'orientation', value)
                          : onThumbnailChange(thumbnail, 'orientation', value)
                      }
                      style={{ width: 120 }}
                      options={ORIENTATION_OPTIONS}
                    ></Select>
                  </Form.Item>
                </Form.Item>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onThumbnailRemove(thumbnail)}
                >
                  delete
                </Button>
              </div>
            </div>
          </Card>
        );
      }}
    />
  );
};

export default ThumbnailList;
